import React, { useState, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { getAllFenceConfigs, FenceConfig, getFenceConfigWithColor } from '../config/fenceConfigs'
import { FenceSystem, useFence } from '../contexts/FenceContext'

interface FenceSelectionPopupProps {
  isOpen: boolean
  onClose: () => void
  onFenceSelect: (fence: FenceSystem | null) => void
  selectedFence?: FenceSystem
}

const FenceSelectionPopup: React.FC<FenceSelectionPopupProps> = ({ 
  isOpen, 
  onClose, 
  onFenceSelect, 
  selectedFence: currentFence 
}) => {
  const { selectColor } = useFence() // 🎨 Dodaję dostęp do selectColor
  const [step, setStep] = useState<'category' | 'size' | 'fence' | 'color'>('category')
  const [selectedCategory, setSelectedCategory] = useState<string>('gladkie')
  const [selectedSize, setSelectedSize] = useState<string>('all')
  const [selectedFence, setSelectedFence] = useState<FenceConfig | null>(null)
  const [selectedColor, setSelectedColor] = useState<string>('')
  const [fenceConfigs, setFenceConfigs] = useState<FenceConfig[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  // 🎯 Ładowanie konfiguracji z JSON-ów
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        setLoading(true)
        const configs = await getAllFenceConfigs()
        setFenceConfigs(configs)
      } catch (error) {
        console.error('❌ Błąd ładowania konfiguracji:', error)
      } finally {
        setLoading(false)
      }
    }

    loadConfigs()
  }, [])

  // Kategorie ogrodzeń
  const categories = [
    { id: 'gladkie', name: 'Gładkie' },
    { id: 'slim', name: 'Slim' },
    // { id: 'royal', name: 'Royal' }, // 🎯 UKRYTE - odkomentuj aby przywrócić
    { id: 'royal-mix', name: 'Royal Mix' },
    // { id: 'lupane', name: 'Łupane' }, // 🎯 UKRYTE - odkomentuj aby przywrócić
    { id: 'muro-mix', name: 'Muro Mix' },
    { id: 'vision', name: 'Vision' }
  ]

  // Filtrowanie po wymiarach
  const sizeFilters = [
    { id: 'all', name: 'Wszystkie rozmiary' },
    { id: 'small', name: 'Małe (25x25 - 28x25)' },
    { id: 'medium', name: 'Średnie (80x25 - 100x30)' },
    { id: 'large', name: 'Duże (120x40)' }
  ]

  // Filtrowanie ogrodzeń
  const filteredFences = fenceConfigs.filter(fence => {
    // 🎯 Obsługa kategorii "royal-mix" i "muro-mix" jako dodatkowe opcje
    let categoryMatch = false
    if (selectedCategory === 'royal-mix') {
      // Pokazuj tylko ogrodzenia Royal Mix (id zawiera "royal-mix", "royal_s50", "royal_p50")
      categoryMatch = fence.id === 'royal-mix' || fence.id?.startsWith('royal_') || fence.id?.startsWith('royal-')
    } else if (selectedCategory === 'muro-mix') {
      // Pokazuj tylko ogrodzenia Muro Mix (id zawiera "lupane-muro-mix" lub zaczyna się od "lupane-muro-")
      categoryMatch = fence.id === 'lupane-muro-mix' || fence.id?.startsWith('lupane-muro-')
    } else {
      // Dla pozostałych kategorii - standardowe filtrowanie po category
      categoryMatch = selectedCategory === 'all' || fence.category === selectedCategory
    }
    
    const sizeMatch = selectedSize === 'all' || 
      (selectedSize === 'small' && fence.dimensions.includes('25x')) ||
      (selectedSize === 'medium' && (fence.dimensions.includes('80x') || fence.dimensions.includes('100x'))) ||
      (selectedSize === 'large' && fence.dimensions.includes('120x'))
    
    return categoryMatch && sizeMatch
  })

  const handleCategorySelect = (category: string) => {
    // 🎯 Użyj flushSync dla natychmiastowej aktualizacji stanu (zapobiega migotaniu)
    flushSync(() => {
      setSelectedCategory(category)
    })
    
    // 🎯 Dla Royal Mix i Muro Mix - automatycznie wybierz odpowiednie ogrodzenie i przejdź do wyboru koloru
    if (category === 'royal-mix') {
      const royalMixFence = fenceConfigs.find(fence => fence.id === 'royal-mix')
      if (royalMixFence) {
        flushSync(() => {
          setSelectedFence(royalMixFence)
          setSelectedColor('')
          setStep('color')
        })
        return
      }
    } else if (category === 'muro-mix') {
      const muroMixFence = fenceConfigs.find(fence => fence.id === 'lupane-muro-mix')
      if (muroMixFence) {
        flushSync(() => {
          setSelectedFence(muroMixFence)
          setSelectedColor('')
          setStep('color')
        })
        return
      }
    }
    
    // 🎯 Pomiń krok wyboru rozmiaru - mamy mało wariantów w każdej kategorii
    // Użytkownik wybierze konkretny model, a nie ogólny rozmiar
    flushSync(() => {
      setSelectedSize('all')
      setStep('fence')
    })
  }

  const handleSizeSelect = (size: string) => {
    setSelectedSize(size)
    setStep('fence')
  }

  const handleFenceSelect = (fence: FenceConfig) => {
    setSelectedFence(fence)
    setSelectedColor('')
    setStep('color')
  }

  const handleColorSelect = async (color: string) => {
    console.log('🎨🎨🎨 DEBUG: FenceSelectionPopup handleColorSelect - wybrano kolor:', color)
    console.log('🎯🎯🎯 DEBUG: FenceSelectionPopup - color === "czekolada"?', color === "czekolada")
    console.log('🎯🎯🎯 DEBUG: FenceSelectionPopup - color === "Czekolada"?', color === "Czekolada")
    console.log('🎯🎯🎯 DEBUG: FenceSelectionPopup - color === "A4 Czekolada"?', color === "A4 Czekolada")
    console.log('🎯🎯🎯 DEBUG: FenceSelectionPopup - color === "czekolada" || color === "Czekolada"?', color === "czekolada" || color === "Czekolada")
    console.log('🎯🎯🎯 DEBUG: FenceSelectionPopup - color === "A4 Czekolada"?', color === "A4 Czekolada")
    setSelectedColor(color)
    if (selectedFence) {
      // 🎯 NOWE: Załaduj konfigurację JSON dla wybranego koloru
      let modelPath = selectedFence.modelPath || ''
      let texturePath = selectedFence.texturePath || ''
      
      try {
        console.log('🔄 FenceSelectionPopup: Ładuję konfigurację JSON dla koloru:', color)
        const fenceConfig = await getFenceConfigWithColor(selectedFence.id, color)
        if (fenceConfig && fenceConfig.colorTextures && fenceConfig.colorTextures[color]) {
          console.log('✅ FenceSelectionPopup: Znaleziono konfigurację dla koloru:', color)
          // 🎯 Użyj modelPath dla wybranego koloru (jeśli istnieje)
          if (fenceConfig.colorTextures[color].modelPath) {
            modelPath = fenceConfig.colorTextures[color].modelPath
            console.log('🎯 FenceSelectionPopup: Zaktualizowana ścieżka modelu DAE:', modelPath)
          }
          // 🎯 Użyj pełnej ścieżki do pliku tekstury dla wybranego koloru
          const materialTexturePath = fenceConfig.colorTextures[color].materialTexture || ''
          // Użyj pełnej ścieżki do pliku tekstury
          texturePath = materialTexturePath || texturePath
          console.log('🎯 FenceSelectionPopup: Zaktualizowana ścieżka tekstur (pełna ścieżka):', texturePath)
        } else {
          console.log('❌ FenceSelectionPopup: Nie znaleziono konfiguracji dla koloru:', color)
        }
      } catch (error) {
        console.error('❌ FenceSelectionPopup: Błąd ładowania konfiguracji JSON:', error)
      }
      
      // Konwertuj FenceConfig na FenceSystem
      const finalFence: FenceSystem = {
        id: selectedFence.id,
        name: selectedFence.name,
        objFile: modelPath,
        mtlFile: modelPath.replace('.dae', '.mtl'),
        textureFile: texturePath,
        daeFile: modelPath,
        texturePath: texturePath,
        basePrice: selectedFence.blockPrice || 150, // 💰 Używam blockPrice jako basePrice
        blockPrice: selectedFence.blockPrice,
        decorationBlockPrice: selectedFence.decorationBlockPrice,
        blockHeight: selectedFence.blockHeight,
        dimensions: selectedFence.dimensions // 📐 Dodaję wymiary bloczka
      }
      
      // 🎨 Ustaw WYBRANY kolor (nie domyślny!)
      console.log(`🎨 DEBUG: FenceSelectionPopup - PRZED selectColor(${color})`)
      selectColor(color) // 🎨 Ustaw WYBRANY kolor w kontekście
      console.log(`🎨 DEBUG: FenceSelectionPopup - PO selectColor(${color})`)
      console.log(`🎨 DEBUG: FenceSelectionPopup - ustawiam WYBRANY kolor: ${color} dla ogrodzenia: ${selectedFence.name}`)
      
      // 🚀 PRELOADING: Załaduj model i tekstury w tle od razu po wyborze koloru
      // Dzięki temu gdy użytkownik kliknie "dodaj element", wszystko będzie już w cache
      import('../utils/threejs/modelLoader').then(({ preloadFenceAssets }) => {
        preloadFenceAssets(selectedFence.id, color)
      })
      
      onFenceSelect(finalFence)
      onClose()
      // Resetuj stan po zamknięciu
      setStep('category')
      setSelectedCategory('gładkie')
      setSelectedSize('all')
      setSelectedFence(null)
      setSelectedColor('')
    }
  }

  const handleBack = () => {
    switch (step) {
      case 'size':
        setStep('category')
        break
      case 'fence':
        // 🎯 Zawsze wracamy do kategorii (bo pomijamy krok wyboru rozmiaru)
        setStep('category')
        break
      case 'color':
        // 🎯 Dla Royal Mix i Muro Mix - wracamy do kategorii (bo pomijamy krok wyboru ogrodzenia)
        if (selectedCategory === 'royal-mix' || selectedCategory === 'muro-mix') {
          setStep('category')
          // Resetuj wybrane ogrodzenie, aby móc wybrać inną kategorię
          setSelectedFence(null)
        } else {
          setStep('fence')
        }
        break
    }
  }

  const handleResetToCategory = () => {
    setStep('category')
    setSelectedCategory('gladkie')
    setSelectedSize('all')
    setSelectedFence(null)
    setSelectedColor('')
  }

  const handleClose = () => {
    onClose()
    // Resetuj stan po zamknięciu
    setStep('category')
    setSelectedCategory('gladkie')
    setSelectedSize('all')
    setSelectedFence(null)
    setSelectedColor('')
  }

  // Jeśli mamy aktualnie wybrane ogrodzenie, ustaw je jako domyślne tylko przy pierwszym otwarciu
  useEffect(() => {
    // 🎯 Nie aktualizuj jeśli użytkownik właśnie wybrał kategorię (zapobiega migotaniu)
    if (currentFence && isOpen && step === 'category' && fenceConfigs.length > 0) {
      // Znajdź ogrodzenie w katalogu i ustaw odpowiednie wartości
      const fenceInCatalog = fenceConfigs.find(f => f.id === currentFence.id)
      if (fenceInCatalog && !selectedFence) {
        // 🎯 Ustaw tylko jeśli nie ma jeszcze wybranego ogrodzenia (zapobiega nadpisywaniu)
        flushSync(() => {
          setSelectedCategory(fenceInCatalog.category)
          setSelectedFence(fenceInCatalog)
          setStep('color')
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFence, isOpen, step, fenceConfigs.length])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-auto max-h-[95vh] flex flex-col mobile-popup">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 md:p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="font-sans text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1">
              Wybierz System Ogrodzenia
            </h2>
            <p className="font-sans text-xs sm:text-sm text-gray-600">
              Skonfiguruj swoje idealne ogrodzenie w kilku krokach
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all touch-manipulation mobile-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 bg-gray-50 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2 sm:space-x-4 overflow-x-auto">
              <div className={`flex items-center space-x-2 ${step === 'category' ? 'text-red-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'category' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  1
                </div>
                <span className="font-sans font-medium text-sm whitespace-nowrap">Kategoria</span>
              </div>
              <div className={`w-4 sm:w-8 h-0.5 ${step === 'fence' || step === 'color' ? 'bg-red-600' : 'bg-gray-300'}`}></div>
              <div className={`flex items-center space-x-2 ${step === 'fence' ? 'text-red-600' : step === 'color' ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'fence' ? 'bg-red-600 text-white' : step === 'color' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  2
                </div>
                <span className="font-sans font-medium text-sm whitespace-nowrap">Ogrodzenie</span>
              </div>
              <div className={`w-4 sm:w-8 h-0.5 ${step === 'color' ? 'bg-red-600' : 'bg-gray-300'}`}></div>
              <div className={`flex items-center space-x-2 ${step === 'color' ? 'text-red-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'color' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  3
                </div>
                <span className="font-sans font-medium text-sm whitespace-nowrap">Kolor</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 md:p-8 overflow-y-auto flex-1 min-h-0">
          {/* Step 1: Kategoria */}
          {step === 'category' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                {currentFence ? (
                  <button
                    onClick={handleClose}
                    className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation mobile-button"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="font-sans font-medium">Anuluj</span>
                  </button>
                ) : (
                  <div className="w-20"></div>
                )}
                <div className="text-center">
                  <h3 className="font-sans text-2xl font-bold text-gray-900 mb-2">Wybierz kategorię ogrodzenia</h3>
                  <p className="font-sans text-gray-600">Jakie ogrodzenie najlepiej pasuje do Twojego domu?</p>
                </div>
                <div className="w-20"></div> {/* Spacer dla wyrównania */}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className="group p-6 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 hover:shadow-lg transition-all text-center transform hover:scale-105 touch-manipulation mobile-button"
                  >
                    <div className="w-12 h-12 bg-gray-100 group-hover:bg-red-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                      <span className="text-xl">🏗️</span>
                    </div>
                    <span className="font-sans font-semibold text-gray-900 group-hover:text-red-700">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Rozmiar */}
          {step === 'size' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="font-sans font-medium">Wstecz</span>
                </button>
                <div className="text-center">
                  <h3 className="font-sans text-2xl font-bold text-gray-900 mb-1">Wybierz rozmiar</h3>
                  <p className="font-sans text-gray-600">Kategoria: <span className="font-semibold text-red-600">{categories.find(c => c.id === selectedCategory)?.name}</span></p>
                </div>
                <div className="w-20"></div> {/* Spacer dla wyrównania */}
              </div>
              <div className="grid grid-cols-1 gap-4">
                {sizeFilters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => handleSizeSelect(filter.id)}
                    className="group p-6 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 hover:shadow-lg transition-all text-left transform hover:scale-[1.02]"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-sans font-semibold text-lg text-gray-900 group-hover:text-red-700">{filter.name}</span>
                        <p className="font-sans text-sm text-gray-500 mt-1">Idealny dla różnych projektów</p>
                      </div>
                      <div className="w-8 h-8 bg-gray-100 group-hover:bg-red-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-400 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Ogrodzenie */}
          {step === 'fence' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="font-sans font-medium">Wstecz</span>
                </button>
                <div className="text-center">
                  <h3 className="font-sans text-2xl font-bold text-gray-900 mb-1">Wybierz ogrodzenie</h3>
                  <p className="font-sans text-gray-600">
                    <span className="font-semibold text-red-600">{categories.find(c => c.id === selectedCategory)?.name}</span> • 
                    <span className="font-semibold text-red-600 ml-1">{sizeFilters.find(s => s.id === selectedSize)?.name}</span>
                  </p>
                </div>
                <div className="w-20"></div> {/* Spacer dla wyrównania */}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredFences.map(fence => (
                  <button
                    key={fence.id}
                    onClick={() => handleFenceSelect(fence)}
                    className="group p-6 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 hover:shadow-lg transition-all text-left transform hover:scale-[1.02]"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-sans font-semibold text-lg text-gray-900 group-hover:text-red-700">{fence.name}</h4>
                      <span className="px-3 py-1 bg-gray-100 group-hover:bg-red-100 text-gray-600 group-hover:text-red-700 text-xs font-medium rounded-full">
                        {fence.dimensions}
                      </span>
                    </div>
                    <p className="font-sans text-sm text-gray-600 mb-4">{fence.colors.length} dostępnych kolorów</p>
                    <div className="flex justify-between items-center pt-3 border-t border-gray-100 group-hover:border-red-100">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${fence.modelPath ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="font-sans text-xs text-gray-500">
                          {fence.modelPath ? 'Model 3D dostępny' : 'Brak modelu 3D'}
                        </span>
                      </div>
                      <span className="font-sans font-bold text-red-600 text-lg">
                        {fence.blockPrice ? `${fence.blockPrice.toFixed(2)} zł/bloczek` : 'Cena na zapytanie'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Kolor */}
          {step === 'color' && selectedFence && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="font-sans font-medium">Wstecz</span>
                </button>
                <div className="text-center">
                  <h3 className="font-sans text-2xl font-bold text-gray-900 mb-1">Wybierz kolor</h3>
                  <p className="font-sans text-gray-600">Ostatni krok - wybierz idealny kolor dla swojego ogrodzenia</p>
                </div>
                <div className="w-20"></div> {/* Spacer dla wyrównania */}
              </div>

              {/* Podsumowanie wybranego ogrodzenia */}
              <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-sans font-bold text-lg text-gray-900 mb-1">{selectedFence.name}</h4>
                    <p className="font-sans text-sm text-gray-600 mb-2">{selectedFence.dimensions}</p>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${selectedFence.modelPath ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="font-sans text-xs text-gray-500">
                        {selectedFence.modelPath ? 'Model 3D dostępny' : 'Brak modelu 3D'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-sans text-sm text-gray-600">Cena bloczka</span>
                    <div className="font-sans font-bold text-2xl text-red-600">
                      {selectedFence.blockPrice ? `${selectedFence.blockPrice.toFixed(2)} zł` : 'na zapytanie'}
                    </div>
                    {selectedFence.blockHeight && (
                      <p className="font-sans text-xs text-gray-500">Wysokość: {selectedFence.blockHeight}cm</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Wybór koloru */}
              <div>
                <h5 className="font-sans font-semibold text-gray-900 mb-4">Dostępne kolory ({selectedFence.colors.length})</h5>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-2 sm:gap-4">
                  {selectedFence.colors.map(color => {
                    // Pobierz ścieżkę do miniatury dla tego koloru
                    const colorData = selectedFence.colorTextures?.[color]
                    let thumbnailPath = colorData?.thumbnail || colorData?.materialTexture || '/placeholder-texture.png'
                    
                    // 🎯 Fallback dla Vision Bet Long: jeśli materialTexture wskazuje na material_1.jpg,
                    // ale plik może nie istnieć, spróbuj alternatywnych nazw
                    if (selectedFence.id === 'vision-bet-long' && colorData?.materialTexture?.includes('material_1.jpg')) {
                      // Dla kolorów 06+ pliki mogą mieć inne nazwy
                      // Spróbuj najpierw oryginalnej ścieżki, jeśli nie działa, użyj alternatyw
                      const baseDir = colorData.materialTexture.substring(0, colorData.materialTexture.lastIndexOf('/'))
                      // Alternatywne nazwy: __.jpg, material_3.jpg, material_4.jpg, itp.
                      thumbnailPath = thumbnailPath // Zostaw oryginalną, onError w img spróbuje fallback
                    }
                    
                    // 🐛 DEBUG: Zawsze loguj dla koloru 14
                    if (color === '14') {
                      console.log(`🔍 FenceSelectionPopup - szczegółowy log dla koloru 14:`, {
                        colorData,
                        thumbnailPath,
                        selectedFenceId: selectedFence.id,
                        hasColorTextures: !!selectedFence.colorTextures,
                        color14InColorTextures: !!selectedFence.colorTextures?.['14'],
                        allColorKeys: selectedFence.colorTextures ? Object.keys(selectedFence.colorTextures).slice(0, 10) : []
                      })
                    }
                    
                    return (
                      <button
                        key={color}
                        onClick={() => handleColorSelect(color)}
                        className="group p-6 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 hover:shadow-lg transition-all text-center transform hover:scale-105"
                      >
                        <div className="flex flex-col items-center space-y-3">
                          {/* Miniaturka tekstury */}
                          <img 
                            src={thumbnailPath}
                            alt={`Tekstura ${color}`}
                            className="w-16 h-16 rounded-lg border-2 border-gray-300 bg-gray-100 object-cover group-hover:border-red-400 group-hover:shadow-md transition-all"
                            onError={(e) => {
                              // Fallback jeśli obraz nie istnieje
                              const target = e.target as HTMLImageElement
                              target.src = '/placeholder-texture.png'
                            }}
                          />
                          {/* Nazwa koloru */}
                          <span className="font-sans font-semibold text-sm text-gray-900 group-hover:text-red-700">{color}</span>
                          {colorData?.colorName && (
                            <span className="font-sans text-xs text-gray-500 group-hover:text-red-500">{colorData.colorName}</span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 sm:p-6 md:p-8 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
          {/* Mobile: Teksty na górze, Przyciski na dole */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 order-1 sm:order-1">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                <p className="font-sans text-base sm:text-sm text-gray-600">
                  Krok {step === 'category' ? 1 : step === 'size' ? 2 : step === 'fence' ? 3 : 4} z 4
                </p>
              </div>
              {step !== 'category' && (
                <span className="font-sans text-sm sm:text-xs text-gray-500">
                  Możesz wrócić i zmienić swój wybór
                </span>
              )}
            </div>
            {/* Mobile: Przyciski na dole, Desktop: na prawo */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-3 w-full sm:w-auto order-2 sm:order-2">
              {step !== 'category' && (
                <button
                  onClick={handleResetToCategory}
                  className="w-full sm:w-auto px-5 py-3 sm:py-2 text-base sm:text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all font-sans font-medium touch-manipulation mobile-button"
                >
                  Zacznij od nowa
                </button>
              )}
              <button
                onClick={handleClose}
                className="w-full sm:w-auto px-5 sm:px-6 py-3 sm:py-2 text-base sm:text-base text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all font-sans font-medium touch-manipulation mobile-button"
              >
                Anuluj
              </button>
              {step === 'color' && (
                <span className="w-full sm:w-auto px-5 py-3 sm:py-2 bg-green-100 text-green-700 rounded-lg font-sans text-base sm:text-sm font-medium text-center">
                  Wybierz kolor aby zakończyć
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FenceSelectionPopup
